/**
 * Interfaz para reconstruir Plan desde persistencia
 */
export interface PlanPersistence {
  id: number;
  nombre: string;
  consultorios: number;
  profesionales: number;
  precio: number;
  active: boolean;
  fechaAlta?: Date | string;
  fechaModificacion?: Date | string;
  usuarioModificacion?: string;
}

export class Plan {
  private constructor(
    private readonly _id: number,
    private readonly _nombre: string,
    private readonly _consultorios: number,
    private readonly _profesionales: number,
    private readonly _precio: number,
    private readonly _active: boolean,
    private readonly _fechaAlta?: Date,
    private readonly _fechaModificacion?: Date,
    private readonly _usuarioModificacion?: string
  ) {}

  /**
   * Crea un nuevo Plan (nueva entidad)
   * Incluye validaciones y lógica de negocio
   */
  static create(
    id: number,
    nombre: string,
    consultorios: number,
    profesionales: number,
    precio: number,
    active: boolean = true,
    fechaAlta?: Date,
    fechaModificacion?: Date,
    usuarioModificacion?: string
  ): Plan {
    // Validaciones
    if (!nombre || nombre.trim().length === 0) {
      throw new Error('Plan name cannot be empty');
    }
    if (consultorios < 0) {
      throw new Error('Consultorios cannot be negative');
    }
    if (profesionales < 0) {
      throw new Error('Profesionales cannot be negative');
    }
    if (precio < 0) {
      throw new Error('Price cannot be negative');
    }

    return new Plan(
      id,
      nombre.trim(),
      consultorios,
      profesionales,
      precio,
      active,
      fechaAlta,
      fechaModificacion,
      usuarioModificacion
    );
  }

  /**
   * Reconstruye un Plan desde persistencia (sin validaciones)
   * Se usa cuando se carga desde BD o API
   */
  static reconstitute(data: PlanPersistence): Plan {
    return new Plan(
      data.id,
      data.nombre,
      data.consultorios,
      data.profesionales,
      data.precio,
      data.active,
      data.fechaAlta 
        ? (typeof data.fechaAlta === 'string' ? new Date(data.fechaAlta) : data.fechaAlta)
        : undefined,
      data.fechaModificacion
        ? (typeof data.fechaModificacion === 'string' ? new Date(data.fechaModificacion) : data.fechaModificacion)
        : undefined,
      data.usuarioModificacion
    );
  }

  // Getters
  get id(): number {
    return this._id;
  }

  get nombre(): string {
    return this._nombre;
  }

  get consultorios(): number {
    return this._consultorios;
  }

  get profesionales(): number {
    return this._profesionales;
  }

  get precio(): number {
    return this._precio;
  }

  get fechaAlta(): Date | undefined {
    return this._fechaAlta;
  }

  get fechaModificacion(): Date | undefined {
    return this._fechaModificacion;
  }

  get usuarioModificacion(): string | undefined {
    return this._usuarioModificacion;
  }

  isActive(): boolean {
    return this._active;
  }

  /**
   * Activa el plan
   * Retorna un nuevo Plan (inmutabilidad)
   */
  activate(): Plan {
    return new Plan(
      this._id,
      this._nombre,
      this._consultorios,
      this._profesionales,
      this._precio,
      true,
      this._fechaAlta,
      this._fechaModificacion,
      this._usuarioModificacion
    );
  }

  /**
   * Desactiva el plan
   * Retorna un nuevo Plan (inmutabilidad)
   */
  deactivate(): Plan {
    return new Plan(
      this._id,
      this._nombre,
      this._consultorios,
      this._profesionales,
      this._precio,
      false,
      this._fechaAlta,
      this._fechaModificacion,
      this._usuarioModificacion
    );
  }

  /**
   * Actualiza el precio del plan
   * Retorna un nuevo Plan (inmutabilidad)
   */
  updatePricing(precio: number): Plan {
    if (precio < 0) {
      throw new Error('Price cannot be negative');
    }

    return new Plan(
      this._id,
      this._nombre,
      this._consultorios,
      this._profesionales,
      precio,
      this._active,
      this._fechaAlta,
      new Date(),
      this._usuarioModificacion
    );
  }
}
